import { formatTranslation, inspectionStatusLabelKey } from 'intl';
import {
  GridColumnField,
  GridColumnFieldCompany,
  GridColumnFieldUser,
  GridColumnFieldIssue,
  GridColumnShape,
  GridColumnWidth,
} from '../types';
import {
  createDateWithTimeCellRenderer,
  createDateCellRenderer,
  createSiteCellRenderer,
  createLevelCellRenderer,
  createDocumentStateCellRenderer,
  createContractPartiesCellRenderer,
} from '../cellRenderers';
import { IntlShape } from 'react-intl';
import { DocumentationTranslationKeys, IssueTranslationKeys, CommonTranslationKeys, InspectionTranslationKeys, ProjectSetupTranslationKeys, UserTranslationKeys, FiltersTranslationKeys } from 'intl/keys';
import {
  createdAtValueGetter,
  createAddressCountryValueGetter,
  modifiedAtValueGetter,
  documentStateValueGetter,
  issueStateValueGetter,
  levelsValueGetter,
  sitesValueGetter,
  authorModifiedByLabelValueGetter,
  authorCreatedByLabelValueGetter,
  processValueGetter,
  executionDateValueGetter,
  inspectionAuthorValueGetter,
  userRoleValueGetter,
  userSitesValueGetter,
  userProcessesValueGetter,
  companySitesValueGetter,
  projectInvolvementTypeValueGetter,
  contractPartiesValueGetter,
} from '../valueGetters/index';
import {
  getDateRangeOperator,
  getDocumentStateOperator,
  getLevelOperator,
  getSiteOperator,
  getCompanyOperator,
  getProcessOperator,
  getStatusOperator,
  getIssueStateOperator,
} from '../customFilters';
import { getGridSingleSelectOperators, GridFilterOperator } from '@mui/x-data-grid-pro';
import {
  getDocumentStateValueOptions,
  getIssueStateFilterValueOptions,
  getProjectInvolvementTypeValueOptions,
  getUserRoleValueOptions,
} from '../valueOptions';
import {
  levelsSortComparator,
  sitesSortComparator,
  processSortComparator,
  userSitesSortComparator,
  userProcessesSortComparator,
  executionDateSortComparator,
} from '../sortComparators';
import { ISO6391LanguageCode } from 'shared/types/locale';
import { HashMap } from 'shared/types/commonView';
import { COLUMN_SHAPES } from './constants';

export { COLUMN_SHAPES };

export function getCreatedAtGridColumnShape(
  intl: IntlShape,
  timezone: string
): GridColumnShape {
  const dateRangeOperator = getDateRangeOperator(intl, timezone);

  return {
    field: GridColumnField.createdAt,
    type: 'date',
    headerNameId: DocumentationTranslationKeys.columnLabelCreatedAt,
    valueGetter: createdAtValueGetter,
    renderCell: createDateWithTimeCellRenderer(
      intl.locale as ISO6391LanguageCode,
      timezone
    ),
    filterOperators: [dateRangeOperator],
  };
}

export function getModifiedAtGridColumnShape(
  intl: IntlShape,
  timezone: string
): GridColumnShape {
  const dateRangeOperator = getDateRangeOperator(intl, timezone);

  return {
    field: GridColumnField.modifiedAt,
    type: 'date',
    headerNameId: DocumentationTranslationKeys.columnLabelModifiedAt,
    valueGetter: modifiedAtValueGetter,
    renderCell: createDateWithTimeCellRenderer(
      intl.locale as ISO6391LanguageCode,
      timezone
    ),
    filterOperators: [dateRangeOperator],
  };
}

export function getModifiedByGridColumnShape(
  organizationId: string,
  intl: IntlShape
): GridColumnShape {
  return {
    field: GridColumnField.modifiedBy,
    type: 'string',
    headerNameId: DocumentationTranslationKeys.columnLabelModifiedBy,
    valueGetter: (_, row) =>
      authorModifiedByLabelValueGetter(row, organizationId, intl),
  };
}

export function getCreatedByGridColumnShape(
  organizationId: string,
  intl: IntlShape
): GridColumnShape {
  return {
    field: GridColumnField.createdBy,
    type: 'string',
    headerNameId: DocumentationTranslationKeys.columnLabelCreatedBy,
    valueGetter: (_, row) =>
      authorCreatedByLabelValueGetter(row, organizationId, intl),
  };
}

export function getSiteGridColumnShape(intl: IntlShape): GridColumnShape {
  const siteOperator = getSiteOperator(intl);
  return {
    field: GridColumnField.sites,
    type: 'string',
    headerNameId: DocumentationTranslationKeys.columnLabelSite,
    valueGetter: sitesValueGetter,
    renderCell: createSiteCellRenderer(intl),
    sortComparator: sitesSortComparator,
    filterOperators: [siteOperator],
    width: GridColumnWidth.large,
  };
}

export function getLevelGridColumnShape(intl: IntlShape): GridColumnShape {
  const levelOperator = getLevelOperator(intl);

  return {
    field: GridColumnField.levels,
    type: 'string',
    headerNameId: DocumentationTranslationKeys.columnLabelLevel,
    valueGetter: levelsValueGetter,
    renderCell: createLevelCellRenderer(intl),
    filterOperators: [levelOperator],
    sortComparator: levelsSortComparator,
    width: GridColumnWidth.large,
  };
}

export function getDocumentStateGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const documentStateOperator = getDocumentStateOperator(intl);

  return {
    field: GridColumnField.state,
    type: 'singleSelect',
    headerNameId: DocumentationTranslationKeys.columnLabelState,
    valueGetter: documentStateValueGetter,
    valueOptions: getDocumentStateValueOptions(intl),
    renderCell: createDocumentStateCellRenderer(),
    filterOperators: [documentStateOperator],
  };
}

export function getIssueStateGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const issueStateOperator = getIssueStateOperator(intl);
  return {
    field: GridColumnFieldIssue.state,
    type: 'singleSelect',
    headerNameId: IssueTranslationKeys.columnLabelState,
    valueGetter: issueStateValueGetter,
    valueOptions: getIssueStateFilterValueOptions(intl),
    renderCell: createDocumentStateCellRenderer(),
    filterOperators: [issueStateOperator],
  };
}

export function getProcessGridColumnShape(
  intl: IntlShape,
  processes?: HashMap<any>
): GridColumnShape {
  const processOperator = getProcessOperator(intl);
  const valueGetter = processes
    ? (_, row: { process: string }) => {
        if (!row?.process) {
          return [];
        }
        const process = processes[row.process];
        return process
          ? [
              {
                _id: row.process,
                label: process.label || process.code || '',
              },
            ]
          : [];
      }
    : processValueGetter;
  return {
    field: GridColumnField.process,
    type: 'string',
    headerNameId: CommonTranslationKeys.process,
    valueGetter,
    sortComparator: processSortComparator,
    filterOperators: [processOperator],
  };
}

export function getInspectionStatusGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const statusOperator = getStatusOperator(intl);
  const valueGetter = (
    _: unknown,
    row: { status: any }
  ): Array<{ _id: string; label: string }> => {
    if (!row?.status) {
      return [];
    }
    return [
      {
        _id: row.status,
        label: formatTranslation(intl, inspectionStatusLabelKey(row.status)),
      },
    ];
  };
  return {
    field: GridColumnField.status,
    type: 'string',
    headerNameId: InspectionTranslationKeys.columnLabelStatus,
    valueGetter,
    sortComparator: processSortComparator,
    filterOperators: [statusOperator],
  };
}

export function getInspectionAuthorGridColumnShape(
  organizationId: string,
  intl: IntlShape
): GridColumnShape {
  return {
    field: GridColumnField.author,
    type: 'string',
    headerNameId: InspectionTranslationKeys.columnLabelAuthor,
    valueGetter: (_, row) => inspectionAuthorValueGetter(_, row, intl),
  };
}

export function getExecutionDateGridColumnShape(
  locale: ISO6391LanguageCode,
  intl: IntlShape,
  timezone: string
): GridColumnShape {
  const dateRangeOperator = getDateRangeOperator(intl, timezone);

  return {
    field: GridColumnField.executionDate,
    type: 'date',
    headerNameId: InspectionTranslationKeys.columnLabelExecutionDate,
    valueGetter: executionDateValueGetter,
    renderCell: createDateCellRenderer(locale, timezone),
    sortComparator: executionDateSortComparator,
    filterOperators: [dateRangeOperator],
  };
}

export function getContractPartiesGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const companyOperator = getCompanyOperator(intl);
  return {
    field: GridColumnField.parties,
    type: 'string',
    headerNameId: ProjectSetupTranslationKeys.parties,
    valueGetter: contractPartiesValueGetter,
    renderCell: createContractPartiesCellRenderer(),
    filterOperators: [companyOperator],
    width: GridColumnWidth.extraLarge,
  };
}

export function getUserRoleGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const isOperator = getGridSingleSelectOperators().find(
    (operator) => operator.value === 'is'
  );

  return {
    field: GridColumnFieldUser.role,
    type: 'singleSelect',
    headerNameId: UserTranslationKeys.managementRole,
    valueGetter: userRoleValueGetter,
    valueOptions: getUserRoleValueOptions(intl),
    filterOperators: [isOperator as GridFilterOperator],
  };
}

export function getUserSitesGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const siteOperator = getSiteOperator(intl);
  return {
    field: GridColumnFieldUser.sites,
    type: 'string',
    headerNameId: FiltersTranslationKeys.filterTypeSite,
    width: GridColumnWidth.extraLarge,
    valueGetter: userSitesValueGetter,
    sortComparator: userSitesSortComparator,
    filterOperators: [siteOperator],
  };
}

export function getUserProcessesGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const processOperator = getProcessOperator(intl);
  return {
    field: GridColumnFieldUser.processes,
    type: 'string',
    headerNameId: CommonTranslationKeys.process,
    width: GridColumnWidth.extraLarge,
    valueGetter: userProcessesValueGetter,
    sortComparator: userProcessesSortComparator,
    filterOperators: [processOperator],
  };
}

export function getProjectInvolvementTypeGridColumnShape(
  intl: IntlShape
): GridColumnShape {
  const isOperator = getGridSingleSelectOperators().find(
    (operator) => operator.value === 'is'
  );
  return {
    field: GridColumnFieldCompany.projectInvolvementType,
    type: 'singleSelect',
    headerNameId: ProjectSetupTranslationKeys.involvementTypeFieldLabel,
    valueGetter: projectInvolvementTypeValueGetter,
    valueOptions: getProjectInvolvementTypeValueOptions(intl),
    filterOperators: [isOperator as GridFilterOperator],
  };
}

export function getCompanySitesGridColumnShape(intl: IntlShape): GridColumnShape {
  const siteOperator = getSiteOperator(intl);
  return {
    field: GridColumnField.sites,
    type: 'string',
    headerNameId: DocumentationTranslationKeys.columnLabelSite,
    valueGetter: companySitesValueGetter,
    sortComparator: sitesSortComparator,
    filterOperators: [siteOperator],
  };
}

export function getCorrespondenceAddressCountryGridColumnShape(
  locale: string
): GridColumnShape {
  return {
    field: GridColumnFieldCompany.correspondenceAddressCountry,
    type: 'string',
    headerNameId: ProjectSetupTranslationKeys.addressCountry,
    valueGetter: createAddressCountryValueGetter('correspondenceAddress', locale),
  };
}

export function getRegisteredAddressCountryGridColumnShape(
  locale: string
): GridColumnShape {
  return {
    field: GridColumnFieldCompany.registeredAddressCountry,
    type: 'string',
    headerNameId: ProjectSetupTranslationKeys.addressCountry,
    valueGetter: createAddressCountryValueGetter('registeredOfficeAddress', locale),
  };
}
